import type { ReactNode } from "react";
import { Image } from "@/components/ui/image";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { MapPin, Recycle } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { PRODUCT_CARD_SIZES } from "./image-sizes";
import { ProductCardPlaceholder } from "./product-card-placeholder";

export function CardArt({
  imageUrl,
  title,
  grade,
  productTypeSlug,
  priority,
  sizes,
}: {
  imageUrl: string | null;
  title: string;
  grade?: string | null;
  productTypeSlug?: string | null;
  priority: boolean;
  sizes?: string;
}) {
  return (
    <div
      className="relative w-full overflow-hidden bg-surface-2 max-md:aspect-[7/6] max-md:rounded-lg md:aspect-[3/2] group-data-[phone-row]/card:max-md:size-24 group-data-[phone-row]/card:max-md:shrink-0"
    >
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={title || "Product Image"}
          fill
          className="object-cover"
          sizes={sizes ?? PRODUCT_CARD_SIZES}
          quality={60}
          loading={priority ? "eager" : undefined}
          fetchPriority={priority ? "high" : undefined}
        />
      ) : (
        <ProductCardPlaceholder productTypeSlug={productTypeSlug} />
      )}
      {grade ? (
        <span
          className="absolute left-2.5 top-2.5 inline-flex h-6 max-w-[calc(100%-3.75rem)] items-center truncate rounded-md bg-surface px-2 text-overline font-semibold uppercase text-foreground group-data-[phone-row]/card:max-md:left-2 group-data-[phone-row]/card:max-md:top-2 group-data-[phone-row]/card:max-md:max-w-[calc(100%-1rem)]"
        >
          {grade}
        </span>
      ) : null}
    </div>
  );
}

export function CardBody({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-3 p-3.5 max-md:gap-1.5 max-md:p-0 max-md:pt-2 group-data-[phone-row]/card:max-md:pt-0"
    >
      {children}
    </div>
  );
}

export function CardSpec({
  productName,
  grade,
  dimensions,
  href,
}: {
  productName: string;
  grade?: string | null;
  dimensions: string | null;
  href?: string;
}) {
  const specLine = [grade, dimensions].filter(Boolean).join(" · ");
  const nameClass =
    "line-clamp-2 text-body font-semibold leading-snug text-foreground max-md:text-sm";
  return (
    <div className="flex flex-col gap-0.5">
      {href ? (
        <HoverPrefetchLink
          href={href}
          data-card-link
          className={`${nameClass} outline-none after:absolute after:inset-0 after:z-10 after:rounded-lg after:content-[''] focus-visible:after:ring-3 focus-visible:after:ring-ring focus-visible:after:ring-inset group-hover:underline max-md:after:rounded-none`}
        >
          {productName}
        </HoverPrefetchLink>
      ) : (
        <div className={nameClass}>{productName}</div>
      )}
      <div className="truncate text-data tabular-nums text-muted-foreground empty:hidden max-md:text-caption">
        {specLine}
      </div>
    </div>
  );
}

export function CardQuantity({ quantity }: { quantity: number | null }) {
  if (quantity === null || quantity <= 0) return null;
  return (
    <span className="flex items-baseline gap-1 whitespace-nowrap text-data max-md:text-caption">
      <span className="font-semibold tabular-nums text-foreground">
        {quantity.toLocaleString("en-US")}
      </span>
      <span className="text-muted-foreground">available</span>
    </span>
  );
}

function CardAvailability({
  quantity,
  inStock,
}: {
  quantity: number | null;
  inStock: boolean;
}) {
  if (quantity !== null && quantity > 0) return <CardQuantity quantity={quantity} />;
  if (!inStock) return null;
  return (
    <span className="text-data font-medium text-foreground max-md:text-caption">In stock</span>
  );
}

export function CardPrice({
  unitPrice,
  freeLabel,
  unitName = "unit",
  minimumOrder = null,
}: {
  unitPrice: string | null;
  freeLabel: string | null;
  unitName?: string;
  minimumOrder?: string | null;
}) {
  if (freeLabel) {
    return (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-2xl font-semibold leading-tight tracking-tight text-success-strong max-md:text-lg">
        <Recycle className="size-4 shrink-0" aria-hidden />
        {freeLabel}
      </span>
    );
  }
  if (!unitPrice) {
    return (
      <span className="text-body font-medium text-muted-foreground">
        Price on request
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-baseline gap-x-2 max-md:gap-x-1.5">
        <span className="whitespace-nowrap text-2xl font-semibold leading-tight tracking-tight tabular-nums text-foreground max-md:text-lg">
          ${unitPrice}
        </span>
        <span className="text-data text-muted-foreground max-md:text-caption">
          / {unitName} at pickup
        </span>
      </span>
      {minimumOrder ? (
        <span className="text-data tabular-nums text-muted-foreground max-md:text-caption">
          {minimumOrder}
        </span>
      ) : null}
    </span>
  );
}

export function CardFacts({
  location,
  quantityAvailable = null,
  inStock = false,
  pickupOnly = false,
  quoteAction,
}: {
  location: string;
  quantityAvailable?: number | null;
  inStock?: boolean;
  pickupOnly?: boolean;
  quoteAction?: ReactNode;
}) {
  return (
    <>
      <div className="mt-auto border-t border-border max-md:hidden" />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="flex items-start gap-1.5 text-data text-muted-foreground max-md:text-caption">
          <MapPin className="mt-0.5 size-4 shrink-0 max-md:hidden" aria-hidden />
          <span className="line-clamp-2 min-w-0">{location || "Location unavailable"}</span>
        </span>
        {quoteAction ?? <CardAvailability quantity={quantityAvailable} inStock={inStock} />}
        {pickupOnly ? (
          <Badge size="sm" variant="warning" className="self-start max-md:hidden">
            Pickup only
          </Badge>
        ) : null}
      </div>
    </>
  );
}
